import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import md5 from 'crypto-js/md5';
import { Tooltip } from '@mui/material';
import apis from '../../utils/apis';
import { avatarSourcePx, gravatarUrl, sizedPictureUrl } from './avatarSource';

// Module-level cache: deduplicates in-flight and resolved profile picture fetches
const profilePicCache = new Map<string, Promise<string>>();

/** Clear one or all cached profile picture entries so the next render re-fetches. */
export function invalidateProfilePicCache(url?: string) {
  if (url) {
    profilePicCache.delete(url);
  } else {
    profilePicCache.clear();
  }
}

function fetchProfilePic(url: string): Promise<string> {
  const cached = profilePicCache.get(url);
  if (cached) return cached;

  const promise = apis
    .get(url, { responseType: 'blob' })
    .then(
      (response) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('FileReader failed'));
          reader.readAsDataURL(response.data);
        })
    )
    .catch((err) => {
      profilePicCache.delete(url); // allow retry on failure
      throw err;
    });

  profilePicCache.set(url, promise);
  return promise;
}

interface ProfileImageProps {
  profilePicture: string | null;
  email: string | null;
  initials: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  size?: string;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const ProfileImage: React.FC<ProfileImageProps> = ({
  profilePicture,
  email,
  initials,
  name,
  firstName,
  lastName,
  size,
  className,
  onClick
}) => {
  const { t } = useTranslation();
  const [useGravatar, setUseGravatar] = useState(false);
  const [profilePicFailed, setProfilePicFailed] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);

  // How many real pixels this avatar needs, from the size it is drawn at.
  // One number drives both source paths, so a change of size can never leave
  // the Gravatar and the uploaded picture at different resolutions.
  const sourcePx = avatarSourcePx(
    size,
    typeof window !== 'undefined' ? window.devicePixelRatio : undefined
  );

  const getInitialsFontSize = () => {
    if (!size) return 'text-xs';
    if (size.includes('w-32') || size.includes('h-32') || size.includes('w-40') || size.includes('h-40')) return 'text-5xl';
    if (size.includes('w-24') || size.includes('h-24')) return 'text-3xl';
    if (size.includes('w-16') || size.includes('h-16')) return 'text-lg';
    if (size.includes('w-8') || size.includes('h-8') || size.includes('w-10') || size.includes('h-10')) return 'text-sm';
    return 'text-xs';
  };

  const displayName = firstName && lastName
    ? `${firstName} ${lastName}`
    : (name || '');

  const handleGravatarError = () => {
    setUseGravatar(false);
  };

  useEffect(() => {
    // Gravatar is the second link in the fallback chain
    // (profile_picture → Gravatar → initials).
    //
    // Activate it when EITHER:
    //   1. No profile_picture URL was supplied at all, OR
    //   2. A URL was supplied but the fetch failed (typical when the backend
    //      over-emits a /user-profile/.../profile-picture URL for users who
    //      don't actually have a stored picture - the endpoint 404s).
    //
    // Without case (2), several backend list serializers (projects,
    // matters, datasources, organizations, tasks) skip Gravatar entirely
    // because they always emit a URL and only signal "no picture" via a
    // 404 response - Gravatar gets bypassed and the user always sees
    // initials.
    if (email && email.trim() && (!profilePicture || profilePicFailed)) {
      setUseGravatar(true);
    } else {
      setUseGravatar(false);
    }
  }, [email, profilePicture, profilePicFailed]);

  // Fetch profile picture through cached helper (deduplicates across all ProfileImage instances)
  useEffect(() => {
    let cancelled = false;

    setProfilePicFailed(false);
    setResolvedSrc(null);

    if (profilePicture && profilePicture.startsWith('/user-profile/')) {
      // Ask for a variant big enough for this avatar. The size hint is a query
      // parameter, so a backend that does not implement it simply serves the
      // default - and if a sized request fails outright (a stricter endpoint
      // rejecting an unknown parameter), fall back to the bare URL before
      // giving up, so this can never be why an avatar vanishes.
      fetchProfilePic(sizedPictureUrl(profilePicture, sourcePx))
        .catch(() => fetchProfilePic(profilePicture))
        .then((dataUrl) => {
          if (!cancelled) setResolvedSrc(dataUrl);
        })
        .catch(() => {
          if (!cancelled) setProfilePicFailed(true);
        });
    } else if (profilePicture) {
      // External URL (e.g. already a full URL) - use directly
      setResolvedSrc(profilePicture);
    }

    return () => {
      cancelled = true;
    };
  }, [profilePicture, sourcePx]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (onClick) {
      onClick(e);
    }
  };

  if (resolvedSrc && !profilePicFailed) {
    return (
      <Tooltip title={displayName || ''}>
        <img
          src={resolvedSrc}
          alt={t('Profile')}
          className={`${size ? size : 'h-7 w-7'} rounded-full object-cover flex-shrink-0 ${className}`}
          // No `imageRendering: crisp-edges`. It turns off the browser's smooth
          // resampling, which is the right call for pixel art and the wrong one
          // for a photograph: a portrait scaled with it goes hard-edged and
          // stippled, which is most of what read as "blurry" at header size.
          width={sourcePx}
          height={sourcePx}
          decoding="async"
          loading="lazy"
          onClick={handleClick}
          onError={() => setProfilePicFailed(true)}
        />
      </Tooltip>
    );
  }

  if (useGravatar && email && email.trim()) {
    const emailHash = md5(email.toLowerCase().trim()).toString();
    const src = gravatarUrl(emailHash, sourcePx);
    return (
      <Tooltip title={displayName || ''}>
        <img
          src={src}
          alt={displayName || 'User avatar'}
          className={`${size ? size : 'h-7 w-7'} rounded-full object-cover flex-shrink-0 ${className}`}
          width={sourcePx}
          height={sourcePx}
          decoding="async"
          loading="lazy"
          onClick={handleClick}
          onError={handleGravatarError}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={displayName || ''}>
      <div
        className={`${size ? size : 'h-7 w-7'} rounded-full bg-primary-600 dark:bg-primary-500 flex items-center justify-center flex-shrink-0 cursor-pointer hover:shadow-lg transition-shadow duration-200 ${className}`}
        style={{ imageRendering: 'auto' }}
        onClick={handleClick}
      >
        <span className={`${getInitialsFontSize()} text-white select-none`}>
          {initials}
        </span>
      </div>
    </Tooltip>
  );
};

export default ProfileImage;
